// src/pages/SystemAccess.tsx
// ✅ UPDATED: Cognito-native user management (no DynamoDB invitations)

import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  User, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  MoreVertical,
  RefreshCw,
  UserPlus,
  Send,
  Trash2,
  Power,
  PowerOff
} from 'lucide-react';
import { useUserAttributes } from '../hooks/useUserAttributes';
import { userProvisioningAPI, TenantUser } from '../services/userProvisioningAPI';
import { tenantManagementAPI } from '../services/tenantManagementAPI';
import { TenantRecord } from '../types/tenant';
import InviteAdminModal from '../components/tenant/InviteAdminModal';

interface SystemUser extends TenantUser {
  displayStatus: 'ACTIVE' | 'INVITED' | 'INACTIVE';
  statusColor: string;
  statusLabel: string;
  lastActivity: string;
}

const SystemAccess: React.FC = () => {
  const { attributes: userAttributes } = useUserAttributes();
  const currentUserRole = userAttributes?.['custom:role'];
  const currentTenantId = userAttributes?.['custom:tenantId'];

  const [users, setUsers] = useState<SystemUser[]>([]);
  const [tenantData, setTenantData] = useState<TenantRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Check if current user can manage system access
  const canManageAccess = currentUserRole === 'SuperAdmin' || currentUserRole === 'OrgAdmin';

  // Get current tenant info for invitations
  const getCurrentTenant = () => {
    if (tenantData) {
      return {
        tenantId: tenantData.tenantId,
        organizationName: tenantData.organizationName
      };
    }
    
    return {
      tenantId: currentTenantId || '',
      organizationName: 'Your Department'
    };
  };

  // Load tenant data
  const loadTenantData = async () => {
    if (!currentTenantId) return;

    try {
      const response = await tenantManagementAPI.getTenant(currentTenantId);
      if (response.success && response.data) {
        setTenantData(response.data.tenant);
      } else {
        console.error('Failed to load tenant data:', response.error);
      }
    } catch (err: any) {
      console.error('Error loading tenant data:', err);
    }
  };

  // Load system access data from Cognito
  const loadSystemAccess = async () => {
    if (!currentTenantId || !canManageAccess) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('Loading system access data for tenant:', currentTenantId);

      const response = await userProvisioningAPI.getTenantUsers(currentTenantId);
      
      if (response.success && response.data) {
        const { users: tenantUsers } = response.data;
        
        // Process users and add display properties
        const processedUsers: SystemUser[] = tenantUsers.map(user => {
          const statusInfo = userProvisioningAPI.formatUserStatus(user.status);
          
          return {
            ...user,
            displayStatus: getDisplayStatus(user.status, user.enabled),
            statusColor: statusInfo.color,
            statusLabel: statusInfo.label,
            lastActivity: user.lastModifiedDate ? formatLastActivity(user.lastModifiedDate) : 'Never'
          };
        });

        setUsers(processedUsers);
      } else {
        throw new Error(response.error?.message || 'Failed to load system access data');
      }
    } catch (err: any) {
      console.error('Error loading system access:', err);
      setError(err.message || 'Failed to load system access data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      await loadTenantData();
      await loadSystemAccess();
    };
    loadData();
  }, [currentTenantId, canManageAccess]);

  // Helper functions
  const getDisplayStatus = (status: string, enabled: boolean): 'ACTIVE' | 'INVITED' | 'INACTIVE' => {
    if (!enabled) return 'INACTIVE';
    if (status === 'CONFIRMED') return 'ACTIVE';
    if (status === 'FORCE_CHANGE_PASSWORD') return 'INVITED';
    return 'INACTIVE';
  };

  const getStatusIcon = (displayStatus: string) => {
    switch (displayStatus) {
      case 'ACTIVE': return CheckCircle;
      case 'INVITED': return Clock;
      case 'INACTIVE': return XCircle;
      default: return AlertTriangle;
    }
  };

  const formatLastActivity = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Action handlers
  const handleResendInvitation = async (email: string, tenantId: string) => {
    setActionLoading(email);
    try {
      const response = await userProvisioningAPI.resendInvitation(email, tenantId);
      if (response.success) {
        await loadSystemAccess();
        alert('Invitation resent successfully - user will receive a new email from noreply@qmleo.com');
      } else {
        throw new Error(response.error?.message || 'Failed to resend invitation');
      }
    } catch (err: any) {
      alert(`Failed to resend invitation: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableUser = async (email: string, tenantId: string) => {
    if (!window.confirm('Are you sure you want to disable this user? They will no longer be able to access the system.')) return;

    setActionLoading(email);
    try {
      const response = await userProvisioningAPI.disableUser(email, tenantId);
      if (response.success) {
        await loadSystemAccess();
        alert('User disabled successfully');
      } else {
        throw new Error(response.error?.message || 'Failed to disable user');
      }
    } catch (err: any) {
      alert(`Failed to disable user: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnableUser = async (email: string, tenantId: string) => {
    setActionLoading(email);
    try {
      const response = await userProvisioningAPI.enableUser(email, tenantId);
      if (response.success) {
        await loadSystemAccess();
        alert('User enabled successfully');
      } else {
        throw new Error(response.error?.message || 'Failed to enable user');
      }
    } catch (err: any) {
      alert(`Failed to enable user: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvitationSent = async () => {
    console.log('Invitation sent, refreshing system access data');
    await loadSystemAccess();
  };

  // Separate users by status
  const activeUsers = users.filter(u => u.displayStatus === 'ACTIVE');
  const invitedUsers = users.filter(u => u.displayStatus === 'INVITED');
  const inactiveUsers = users.filter(u => u.displayStatus === 'INACTIVE');

  // Render user card
  const renderUser = (user: SystemUser) => {
    const StatusIcon = getStatusIcon(user.displayStatus);
    const canResend = userProvisioningAPI.canResendInvitation(user.status);
    const isActive = user.displayStatus === 'ACTIVE';
    
    return (
      <div key={user.userId} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                user.displayStatus === 'ACTIVE' ? 'bg-green-100' : 
                user.displayStatus === 'INVITED' ? 'bg-yellow-100' : 
                'bg-gray-100'
              }`}>
                <User className={`w-5 h-5 ${
                  user.displayStatus === 'ACTIVE' ? 'text-green-600' : 
                  user.displayStatus === 'INVITED' ? 'text-yellow-600' : 
                  'text-gray-600'
                }`} />
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </h3>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${user.statusColor}`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {user.statusLabel}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Shield className="w-4 h-4" />
                  <span>{user.role}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="text-right text-sm text-gray-500">
              <div>Last Modified:</div>
              <div className="font-medium">{user.lastActivity}</div>
            </div>
            
            <div className="flex space-x-2">
              {/* Resend Invitation Button - only for FORCE_CHANGE_PASSWORD status */}
              {canResend && (
                <button
                  onClick={() => handleResendInvitation(user.email, currentTenantId || '')}
                  disabled={actionLoading === user.email}
                  className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                  title="Resend invitation email"
                >
                  {actionLoading === user.email ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Send className="w-4 h-4 mr-1" />
                  )}
                  Resend
                </button>
              )}
              
              {/* Enable/Disable Button */}
              {user.enabled ? (
                <button
                  onClick={() => handleDisableUser(user.email, currentTenantId || '')}
                  disabled={actionLoading === user.email}
                  className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                  title="Disable user access"
                >
                  <PowerOff className="w-4 h-4 mr-1" />
                  Disable
                </button>
              ) : (
                <button
                  onClick={() => handleEnableUser(user.email, currentTenantId || '')}
                  disabled={actionLoading === user.email}
                  className="inline-flex items-center px-3 py-1 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                  title="Enable user access"
                >
                  <Power className="w-4 h-4 mr-1" />
                  Enable
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  if (!canManageAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to manage system access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Access Management</h1>
              <p className="mt-2 text-gray-600">
                Manage user accounts for {tenantData?.organizationName || 'your department'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={loadSystemAccess}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={() => setInviteModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">{activeUsers.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Setup</p>
                <p className="text-2xl font-bold text-gray-900">{invitedUsers.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
            <span className="text-gray-600">Loading system access data...</span>
          </div>
        ) : (
          <>
            {/* Active Users Section */}
            {activeUsers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Users</h2>
                <div className="space-y-4">
                  {activeUsers.map(renderUser)}
                </div>
              </div>
            )}

            {/* Invited Users Section */}
            {invitedUsers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Pending Setup ({invitedUsers.length})
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    These users have been invited but haven't completed their account setup yet. 
                    Invitation emails are sent from <strong>noreply@qmleo.com</strong>.
                  </p>
                </div>
                <div className="space-y-4">
                  {invitedUsers.map(renderUser)}
                </div>
              </div>
            )}

            {/* Inactive Users Section */}
            {inactiveUsers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Inactive Users</h2>
                <div className="space-y-4">
                  {inactiveUsers.map(renderUser)}
                </div>
              </div>
            )}

            {/* No Users State */}
            {users.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Yet</h3>
                <p className="text-gray-600 mb-4">
                  Get started by inviting your first user to access the system.
                </p>
                <button
                  onClick={() => setInviteModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite First User
                </button>
              </div>
            )}
          </>
        )}

      </div>

      {/* Invite User Modal */}
      <InviteAdminModal
        isOpen={inviteModalOpen}
        tenant={getCurrentTenant()}
        onClose={() => setInviteModalOpen(false)}
        onInvitationSent={handleInvitationSent}
      />
    </div>
  );
};

export default SystemAccess;
